import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import noop from 'utils/noop';
import {
  DestinationTableContextState,
  DestinationTableFilters,
  Destination,
  DESTINATION_TABLE_COLUMN,
} from '../types';
import { getDestinationDataState } from '../ducks/selectors';
import { fetchDestinationsRequest } from '../ducks/actions';
import {
  applySearchFilter,
  applyFilters,
  applySorting,
  applyPagination,
  calculatePaginationInfo,
} from '../utils/tableHelpers';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';

/**
 * ARCHITECTURE NOTES:
 *
 * This context uses a hybrid Redux + Local State approach:
 *
 * Redux Responsibilities:
 * - Data fetching (API calls)
 * - Loading states
 * - Error states
 * - Raw data storage (immutable)
 *
 * Context Responsibilities:
 * - Local data copy (mutable for UI operations)
 * - Search/filter/sort/pagination state
 * - UI interactions and derived state
 *
 * Benefits:
 * - Performance: No Redux updates on every filter/search change
 * - Separation of concerns: Redux = data layer, Context = UI layer
 * - Reduced complexity: No derived state in Redux
 * - Better UX: Instant UI updates without Redux dispatch overhead
 */

const DestinationTableContext = createContext<DestinationTableContextState | null>(null);

// Default values
const DEFAULT_FILTERS: DestinationTableFilters = [];

const DEFAULT_SORT_BY = DESTINATION_TABLE_COLUMN.LAST_MODIFIED;
const DEFAULT_SORT_DIRECTION = SORT_DIRECTIONS.DESCENDING;
const DEFAULT_PAGE_SIZE = 10;

// Export initial state for use in mocks and tests
export const initialDestinationTableContextState: DestinationTableContextState = {
  rawDestinations: [],
  loading: true,
  error: false,
  refetch: noop,
  searchQuery: '',
  setSearchQuery: noop,
  filters: DEFAULT_FILTERS,
  toggleFilter: noop,
  clearFilters: noop,
  sortBy: DEFAULT_SORT_BY,
  sortDirection: DEFAULT_SORT_DIRECTION,
  setSortBy: noop,
  setSortDirection: noop,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
  setPage: noop,
  setPageSize: noop,
  filteredDestinations: [],
  displayedDestinations: [],
  resetAllFilters: noop,
  resetToFirstPage: noop,
};

interface DestinationTableProviderProps {
  children: React.ReactNode;
}

export const DestinationTableProvider: React.FC<DestinationTableProviderProps> = ({ children }) => {
  // Redux integration (only for loading/error/raw data)
  const dispatch = useDispatch();
  const reduxState = useSelector(getDestinationDataState);

  // Safely extract Redux state with fallbacks
  const loading = reduxState?.loading || false;
  const error = reduxState?.error || false;
  const reduxRawData = reduxState?.destinations || [];

  // Local context state for table data (copy from Redux)
  const [localDestinations, setLocalDestinations] = useState<Destination[]>([]);

  // Sync Redux data to local state when Redux data changes
  useEffect(() => {
    if (reduxRawData && Array.isArray(reduxRawData)) {
      setLocalDestinations([...reduxRawData]);
    }
  }, [reduxRawData]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state - now using string array
  const [filters, setFiltersState] = useState<DestinationTableFilters>(DEFAULT_FILTERS);

  // Sort state
  const [sortBy, setSortBy] = useState<DESTINATION_TABLE_COLUMN>(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTIONS>(DEFAULT_SORT_DIRECTION);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Ref to track previous search/filter values to prevent unnecessary resets
  const prevSearchAndFiltersRef = useRef({ searchQuery: '', filters: DEFAULT_FILTERS });

  // Helper to handle filter selection changes
  const toggleFilter = useCallback((filterId: string) => {
    setFiltersState((prevFilters) => {
      const newFilters = [...prevFilters];
      const filterIndex = newFilters.indexOf(filterId);

      if (filterIndex > -1) {
        // Filter exists, remove it
        newFilters.splice(filterIndex, 1);
      } else {
        // Filter doesn't exist, add it
        newFilters.push(filterId);
      }

      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  // Helper to clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, []);

  // Helper to set page
  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Helper to set page size and reset to first page
  const setPageSizeAndReset = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // Helper to reset all filters and pagination
  const resetAllFilters = useCallback(() => {
    setSearchQuery('');
    setFiltersState(DEFAULT_FILTERS);
    setSortBy(DEFAULT_SORT_BY);
    setSortDirection(DEFAULT_SORT_DIRECTION);
    setCurrentPage(1);
  }, []);

  // Helper to reset to first page
  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Auto-reset pagination when search or filters change
  useEffect(() => {
    const prev = prevSearchAndFiltersRef.current;
    const searchChanged = prev.searchQuery !== searchQuery;
    const filtersChanged = JSON.stringify(prev.filters) !== JSON.stringify(filters);

    if (searchChanged || filtersChanged) {
      setCurrentPage(1);
      prevSearchAndFiltersRef.current = { searchQuery, filters };
    }
  }, [searchQuery, filters]);

  // Custom setSearchQuery that resets pagination
  const setSearchQueryWithReset = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  // Memoized filter pipeline
  const filteredDestinations = useMemo(() => {
    let result = localDestinations;

    // Apply search filter
    result = applySearchFilter(result, searchQuery);

    // Apply filters
    result = applyFilters(result, filters);

    // Apply sorting
    result = applySorting(result, sortBy, sortDirection);

    return result;
  }, [localDestinations, searchQuery, filters, sortBy, sortDirection]);

  // Memoized pagination
  const displayedDestinations = useMemo(() => {
    return applyPagination(filteredDestinations, currentPage, pageSize);
  }, [filteredDestinations, currentPage, pageSize]);

  // Memoized pagination info
  const paginationInfo = useMemo(() => {
    return calculatePaginationInfo(filteredDestinations.length, currentPage, pageSize);
  }, [filteredDestinations.length, currentPage, pageSize]);

  // Context value
  const contextValue: DestinationTableContextState = {
    // Raw data
    rawDestinations: localDestinations,
    loading,
    error,
    refetch: () => dispatch(fetchDestinationsRequest()),

    // Search state
    searchQuery,
    setSearchQuery: setSearchQueryWithReset,

    // Filter state
    filters,
    toggleFilter,
    clearFilters,

    // Sort state
    sortBy,
    sortDirection,
    setSortBy,
    setSortDirection,

    // Pagination state
    currentPage,
    pageSize,
    totalItems: filteredDestinations.length,
    totalPages: paginationInfo.totalPages,
    setPage,
    setPageSize: setPageSizeAndReset,

    // Computed data
    filteredDestinations,
    displayedDestinations,

    // Helper actions
    resetAllFilters,
    resetToFirstPage,
  };

  return (
    <DestinationTableContext.Provider value={contextValue}>
      {children}
    </DestinationTableContext.Provider>
  );
};

// Custom hook to use the context
export const useDestinationTable = (): DestinationTableContextState => {
  const context = useContext(DestinationTableContext);
  if (!context) {
    throw new Error('useDestinationTable must be used within a DestinationTableProvider');
  }
  return context;
};
